import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { LocationProfile } from '@/types';
import * as bookingApi from '@/services/bookingApi';
import { useAuth } from '@/hooks/useAuth';

const STORAGE_KEY = 'current_profile_id';

interface ProfileLocationContextValue {
  locations: LocationProfile[];
  currentProfileId: string | null;
  currentProfile: LocationProfile | null;
  isLoading: boolean;
  error: string | null;
  setCurrentProfileId: (id: string) => void;
  createLocation: (businessName: string) => Promise<LocationProfile>;
  refresh: () => Promise<void>;
  linkProfile: (profileId: string, linkedProfileId: string) => Promise<void>;
  unlinkProfile: (profileId: string, linkedProfileId: string) => Promise<void>;
  makePrimary: (profileId: string) => Promise<void>;
}

const ProfileLocationContext = createContext<ProfileLocationContextValue | null>(null);

export function ProfileLocationProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [locations, setLocations] = useState<LocationProfile[]>([]);
  const [currentProfileId, setCurrentProfileIdState] = useState<string | null>(() =>
    localStorage.getItem(STORAGE_KEY)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setLocations([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const profiles = await bookingApi.getUserProfiles();
      setLocations(profiles);

      const storedId = localStorage.getItem(STORAGE_KEY);
      const validStored = storedId && profiles.some((p) => p.id === storedId);
      const primary = profiles.find((p) => p.is_primary);
      const nextId = validStored ? storedId! : primary?.id ?? profiles[0]?.id ?? null;

      if (nextId) {
        localStorage.setItem(STORAGE_KEY, nextId);
        setCurrentProfileIdState(nextId);
      } else {
        localStorage.removeItem(STORAGE_KEY);
        setCurrentProfileIdState(null);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load locations';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setCurrentProfileId = useCallback((id: string) => {
    localStorage.setItem(STORAGE_KEY, id);
    setCurrentProfileIdState(id);
  }, []);

  const createLocation = useCallback(async (businessName: string) => {
    const profile = await bookingApi.createUserProfile({ business_name: businessName });
    await refresh();
    setCurrentProfileId(profile.id);
    return profile;
  }, [refresh, setCurrentProfileId]);

  const linkProfile = useCallback(async (profileId: string, linkedProfileId: string) => {
    await bookingApi.linkProfile(profileId, linkedProfileId);
    await refresh();
  }, [refresh]);

  const unlinkProfile = useCallback(async (profileId: string, linkedProfileId: string) => {
    await bookingApi.unlinkProfile(profileId, linkedProfileId);
    await refresh();
  }, [refresh]);

  const makePrimary = useCallback(async (profileId: string) => {
    await bookingApi.makeProfilePrimary(profileId);
    await refresh();
    setCurrentProfileId(profileId);
  }, [refresh, setCurrentProfileId]);

  const currentProfile = useMemo(
    () => locations.find((p) => p.id === currentProfileId) ?? null,
    [locations, currentProfileId]
  );

  const value = useMemo(
    () => ({
      locations,
      currentProfileId,
      currentProfile,
      isLoading,
      error,
      setCurrentProfileId,
      createLocation,
      refresh,
      linkProfile,
      unlinkProfile,
      makePrimary,
    }),
    [
      locations,
      currentProfileId,
      currentProfile,
      isLoading,
      error,
      setCurrentProfileId,
      createLocation,
      refresh,
      linkProfile,
      unlinkProfile,
      makePrimary,
    ]
  );

  return (
    <ProfileLocationContext.Provider value={value}>
      {children}
    </ProfileLocationContext.Provider>
  );
}

export function useProfileLocation() {
  const context = useContext(ProfileLocationContext);
  if (!context) {
    throw new Error('useProfileLocation must be used within ProfileLocationProvider');
  }
  return context;
}
